import { cleanupDropZone, initDropZone } from 'util/drop_zone'

import { MEDIA_QUERIES } from 'design'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { WelcomeVideo } from '../../components/live_chat_widget/welcome_video'
import { WidgetHeaderBar } from '../../components/live_chat_widget/widget_header_bar'
import { useCallManager } from '../../contexts/call_manager_context'
import { useNavAndClearSelected } from '../../hooks/app_location/use_nav_and_clear_selected'
import usePreviousProps from '../../hooks/previous_props_hook'
import { useAxios } from '../../hooks/use_axios'
import { useMediaQuery } from '../../hooks/use_media_query'
import { usePersistedState } from '../../hooks/use_persisted_state'
import { getCurrentMember } from '../../reduxes/slices/current/selectors'
import {
  getCurrentQuestion,
  getOrderOfQuestions,
  getQuestions,
} from '../../reduxes/slices/questions/selectors'
import {
  getSelectedTopicId,
  getSelectedWelcomeMessageId,
} from '../../reduxes/slices/selected/selectors'
import { fetchLiveChatTopics } from '../../reduxes/slices/topics/actions'
import { getLiveChatTopics } from '../../reduxes/slices/topics/selectors'
import { getPrevWelcomeMessageId } from '../../reduxes/slices/welcome_messages/selectors'
import { useSelectorDelegateStore } from '../../reduxes/stores/root_store/hooks'
import {
  AppLocationContext,
  TOPIC_VIEW,
  WIDGET_VIEW,
} from '../admin/app_location_provider'
import { AudioOptionsPage } from './audio_options_page'
import { WIDGET_LAYOUT } from './constants'
import { LandingMessengerPage } from './landing_messenger_page'
import { LandingPage } from './landing_page'
import { MediaRecorderPage } from './media_recorder_page'
import { PastConversationsPage } from './past_conversations_page'
import { EmailCapture } from './questions/email_capture'
import { Link } from './questions/link'
import { MultiChoice } from './questions/multi_choice'
import { NameCapture } from './questions/name_capture'
import { Product } from './questions/product'
import { Products } from './questions/products'
import { TextInput } from './questions/text_input'
import { TopicPage } from './topic_page'
import { VideoOptionsPage } from './video_options_page'
import { WelcomePage } from './welcome_page'
import { WidgetPageContainer } from './widget_page_container'

type Props = {
  welcomeVideoPlayed: boolean
  setWelcomeVideoPlayed: any
  widgetLayout: WIDGET_LAYOUT
  setWidgetLayout: any
  toggleLayout: any
  isSelectedInCollection: boolean
  positionInCollection: number | undefined
  collectionReady: boolean
  collectionMuted: boolean
  prevCarouselScrollLeft: any
  isCollectionIntersecting: boolean
}

export const WidgetPageRouter = ({
  welcomeVideoPlayed,
  setWelcomeVideoPlayed,
  widgetLayout,
  setWidgetLayout,
  toggleLayout,
  isSelectedInCollection,
  positionInCollection,
  collectionReady,
  collectionMuted,
  prevCarouselScrollLeft,
  isCollectionIntersecting,
}: Props) => {
  const currentMember = useSelectorDelegateStore(getCurrentMember)
  const { appLocation, setAppLocation, ready } = useContext(AppLocationContext)
  const appLocationPrevRender = usePreviousProps(appLocation)
  const prevReady = usePreviousProps(ready)
  const navAndClearSelected = useNavAndClearSelected()
  const dispatch = useDispatch()
  const axios = useAxios()
  const topics: any = useSelector(getLiveChatTopics)
  const selectedTopicId = useSelector(getSelectedTopicId)
  const currentQuestion: any = useSelector(getCurrentQuestion)
  const prevCurrentQuestion = usePreviousProps(currentQuestion)
  const order = useSelector(getOrderOfQuestions)
  const noOfTopics = topics.length
  const prevNoOfTopics = usePreviousProps(noOfTopics)
  const {
    activeCall: { callRecord, navToVideoView },
  } = useCallManager()
  const [callQuestionId, setCallQuestionId] = useState()
  const rootWelcomeMessageId = useSelectorDelegateStore((state) =>
    parseInt(state.initialAppLocation.welcomeMessageId),
  )
  const currentWelcomeMessageId = useSelector(getSelectedWelcomeMessageId)
  const previousWelcomeMessageId = useSelector(getPrevWelcomeMessageId)
  const [questionOfPrevWelcomeMessage, setQuestionOfPrevWelcomeMessage] =
    usePersistedState('questionOfPrevWelcomeMessage')
  const [firstRenderDone, setFirstRenderDone] = useState(false)
  const questions = useSelector(getQuestions)
  const desktop = useMediaQuery(MEDIA_QUERIES.md, window.parent)

  // when a question is deleted, the widget gets stuck on a blank screen if that question is selected. Reset widget to the root question
  useEffect(() => {
    if (!currentQuestion && questions.length) {
      setAppLocation({
        welcomeMessageId: rootWelcomeMessageId,
        widgetQuestionId: 'root',
        widgetView: null,
      })
    }
  }, [currentQuestion, questions.length, rootWelcomeMessageId, setAppLocation])

  // used to remember the previous welcome message
  useEffect(() => {
    if (!firstRenderDone) {
      setFirstRenderDone(true)
      return
    }
    if (previousWelcomeMessageId) {
      setQuestionOfPrevWelcomeMessage(prevCurrentQuestion)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousWelcomeMessageId])

  useEffect(() => {
    if (currentMember) {
      dispatch(axios(fetchLiveChatTopics()))
    }
  }, [axios, currentMember, dispatch])

  useEffect(() => {
    if (appLocation.widgetView === WIDGET_VIEW.LANDING_MESSENGER) {
      initDropZone()
      return () => {
        cleanupDropZone()
      }
    }
  }, [appLocation.widgetView])

  useEffect(() => {
    if (callRecord) {
      if (!callQuestionId) {
        setCallQuestionId(currentQuestion.id)
      }
    } else {
      setCallQuestionId(undefined)
    }
  }, [callQuestionId, callRecord, currentQuestion?.id])

  useEffect(() => {
    if (
      appLocationPrevRender?.topicView === TOPIC_VIEW.CHAT_VIEW &&
      appLocation.topicView === TOPIC_VIEW.VIDEO_VIEW &&
      callQuestionId
    ) {
      setAppLocation({ widgetQuestionId: callQuestionId })
    }
  }, [
    appLocation.topicView,
    appLocationPrevRender?.topicView,
    callQuestionId,
    setAppLocation,
  ])

  useEffect(() => {
    if (
      ready &&
      currentQuestion &&
      (currentQuestion !== prevCurrentQuestion ||
        ready !== prevReady ||
        noOfTopics !== prevNoOfTopics)
    ) {
      let newView
      switch (currentQuestion.question_type) {
        case 'multi_choice':
          newView = WIDGET_VIEW.MULTI_CHOICE
          break
        case 'call':
          if (
            appLocation.widgetView === WIDGET_VIEW.VIDEO_OPTIONS ||
            appLocation.widgetView === WIDGET_VIEW.AUDIO_OPTIONS
          ) {
            return
          } else if (callRecord) {
            navToVideoView()
            return
          } else if (topics.length) {
            newView = WIDGET_VIEW.LANDING
          } else {
            newView = WIDGET_VIEW.WELCOME
          }
          break
        case 'email_capture':
          newView = WIDGET_VIEW.EMAIL_CAPTURE
          break
        case 'name_capture':
          newView = WIDGET_VIEW.NAME_CAPTURE
          break
        case 'chat':
          newView = WIDGET_VIEW.LANDING_MESSENGER
          break
        case 'products':
          newView = WIDGET_VIEW.PRODUCTS
          break
        case 'text_input':
          newView = WIDGET_VIEW.TEXT_INPUT
          break
        case 'link':
          newView = WIDGET_VIEW.LINK
          break
      }
      setAppLocation({
        widgetView: newView,
      })
    }
  }, [
    currentQuestion,
    prevCurrentQuestion,
    setAppLocation,
    ready,
    prevReady,
    topics.length,
    noOfTopics,
    prevNoOfTopics,
    appLocation.widgetView,
    callRecord,
    navToVideoView,
  ])

  const goTo = useCallback(
    (event: any) => {
      setAppLocation({
        widgetView: event.currentTarget.value,
      })
    },
    [setAppLocation],
  )

  const goBack = useCallback(() => {
    if (
      !currentQuestion ||
      (currentQuestion.root &&
        currentQuestion.welcome_message_id !== rootWelcomeMessageId)
    ) {
      setAppLocation({
        welcomeMessageId: previousWelcomeMessageId,
        widgetQuestionId: questionOfPrevWelcomeMessage?.id || 'root',
        widgetView: null,
      })
      return
    }
    let newState = {}
    switch (appLocation.widgetView) {
      case WIDGET_VIEW.MESSENGER:
      case WIDGET_VIEW.LANDING_MESSENGER:
        navAndClearSelected('/')
        if (
          topics.length > 1 ||
          (currentQuestion.question_type === 'chat' && topics.length >= 1)
        ) {
          newState = { widgetView: WIDGET_VIEW.PAST_CONVERSATIONS }
        } else {
          newState =
            currentQuestion.question_type === 'chat'
              ? {
                  widgetView: null,
                  // eslint-disable-next-line
                  // @ts-ignore
                  widgetQuestionId: order[appLocation.widgetQuestionId].before,
                }
              : { widgetView: WIDGET_VIEW.LANDING }
        }
        break
      case WIDGET_VIEW.PAST_CONVERSATIONS:
        newState =
          currentQuestion.question_type === 'chat'
            ? {
                widgetView: null,
                // eslint-disable-next-line
                // @ts-ignore
                widgetQuestionId: order[appLocation.widgetQuestionId].before,
              }
            : { widgetView: WIDGET_VIEW.LANDING }
        break
      case WIDGET_VIEW.VIDEO_RECORDER:
        if (selectedTopicId) {
          newState = { widgetView: WIDGET_VIEW.MESSENGER }
        } else {
          newState = { widgetView: WIDGET_VIEW.VIDEO_OPTIONS }
        }
        break
      case WIDGET_VIEW.AUDIO_RECORDER:
        if (selectedTopicId) {
          newState = { widgetView: WIDGET_VIEW.MESSENGER }
        } else {
          newState = { widgetView: WIDGET_VIEW.AUDIO_OPTIONS }
        }
        break
      case WIDGET_VIEW.MULTI_CHOICE:
      case WIDGET_VIEW.EMAIL_CAPTURE:
      case WIDGET_VIEW.NAME_CAPTURE:
      case WIDGET_VIEW.WELCOME:
      case WIDGET_VIEW.LANDING:
      case WIDGET_VIEW.PRODUCTS:
      case WIDGET_VIEW.LINK:
      case WIDGET_VIEW.TEXT_INPUT:
        newState = appLocation.productURL
          ? { productURL: null }
          : {
              widgetView: null,
              // eslint-disable-next-line
              // @ts-ignore
              widgetQuestionId: order[appLocation.widgetQuestionId].before,
            }
        break
      default:
        if (topics.length) {
          newState = { widgetView: WIDGET_VIEW.LANDING }
        } else {
          newState = { widgetView: WIDGET_VIEW.WELCOME }
        }
    }
    setAppLocation(newState)
  }, [
    appLocation.productURL,
    appLocation.widgetQuestionId,
    appLocation.widgetView,
    currentQuestion,
    navAndClearSelected,
    order,
    previousWelcomeMessageId,
    questionOfPrevWelcomeMessage?.id,
    rootWelcomeMessageId,
    selectedTopicId,
    setAppLocation,
    topics.length,
  ])

  const [page, showWelcomeVideo] = useMemo(() => {
    if (!currentQuestion) {
      return [<WidgetHeaderBar />, true]
    }
    if (
      widgetLayout === WIDGET_LAYOUT.FLOATING ||
      (collection &&
        ((!carouselContent && widgetLayout !== WIDGET_LAYOUT.FULLSCREEN) ||
          (desktop &&
            widgetLayout === WIDGET_LAYOUT.FULLSCREEN &&
            positionInCollection !== 0)))
    ) {
      return [null, true]
    }
    switch (appLocation.widgetView) {
      case WIDGET_VIEW.WELCOME:
        return [<WelcomePage />, true]
      case WIDGET_VIEW.LANDING:
        return [<LandingPage />, true]
      case WIDGET_VIEW.PAST_CONVERSATIONS:
        return [<PastConversationsPage />, false]
      case WIDGET_VIEW.VIDEO_RECORDER:
      case WIDGET_VIEW.AUDIO_RECORDER:
        return [<MediaRecorderPage />, false]
      case WIDGET_VIEW.LANDING_MESSENGER:
        return [<LandingMessengerPage />, false]
      case WIDGET_VIEW.MESSENGER:
        return [<TopicPage />, false]
      case WIDGET_VIEW.VIDEO_OPTIONS:
        return [<VideoOptionsPage />, false]
      case WIDGET_VIEW.AUDIO_OPTIONS:
        return [<AudioOptionsPage />, false]
      case WIDGET_VIEW.MULTI_CHOICE:
        return [<MultiChoice />, true]
      case WIDGET_VIEW.EMAIL_CAPTURE:
        return [<EmailCapture />, true]
      case WIDGET_VIEW.NAME_CAPTURE:
        return [<NameCapture />, true]
      case WIDGET_VIEW.LINK:
        return [<Link />, true]
      case WIDGET_VIEW.PRODUCTS:
        return [
          appLocation.productURL ? <Product /> : <Products />,
          !appLocation.productURL,
        ]
      case WIDGET_VIEW.TEXT_INPUT:
        return [<TextInput />, true]
      default:
        return [null, true]
    }
  }, [
    appLocation.productURL,
    appLocation.widgetView,
    currentQuestion,
    desktop,
    positionInCollection,
    widgetLayout,
  ])

  const canGoBack = useMemo(() => {
    if (!currentQuestion) {
      return currentWelcomeMessageId !== rootWelcomeMessageId
    }
    if (
      currentQuestion.root &&
      currentQuestion.welcome_message_id !== rootWelcomeMessageId
    ) {
      return true
    }
    switch (currentQuestion.question_type) {
      case 'call':
        return (
          !currentQuestion.root ||
          (appLocation.widgetView !== WIDGET_VIEW.WELCOME &&
            appLocation.widgetView !== WIDGET_VIEW.LANDING)
        )
      case 'chat':
        return (
          !currentQuestion.root ||
          (topics.length &&
            appLocation.widgetView !== WIDGET_VIEW.PAST_CONVERSATIONS)
        )
      case 'products':
        return !currentQuestion.root || !!appLocation.productURL
      default:
        return !currentQuestion.root
    }
  }, [
    appLocation.productURL,
    appLocation.widgetView,
    currentQuestion,
    currentWelcomeMessageId,
    rootWelcomeMessageId,
    topics.length,
  ])

  const handleAnswerAction = useCallback(
    (answer) => {
      if (answer.action.includes('question:')) {
        const id = answer.action.replace('question:', '')
        setAppLocation({
          widgetQuestionId: id,
          widgetView: null,
        })
      } else if (answer.action.includes('welcome_message:')) {
        const id = answer.action.replace('welcome_message:', '')
        setAppLocation({
          welcomeMessageId: id,
          widgetQuestionId: 'root',
          widgetView: null,
        })
      }
    },
    [setAppLocation],
  )

  return (
    <WidgetContext.Provider
      value={{
        goBack,
        canGoBack,
        goTo,
        welcomeVideoPlayed,
        setWelcomeVideoPlayed,
        handleAnswerAction,
        widgetLayout,
        setWidgetLayout,
        toggleLayout,
        isSelectedInCollection,
        positionInCollection,
        collectionReady,
        collectionMuted,
        prevCarouselScrollLeft,
        isCollectionIntersecting,
      }}
    >
      {showWelcomeVideo ? (
        <WidgetPageContainer>
          <WelcomeVideo />
          {page}
        </WidgetPageContainer>
      ) : (
        page
      )}
    </WidgetContext.Provider>
  )
}

// eslint-disable-next-line
// @ts-ignore
const { collection, carouselContent } = window.speakeasySettings

export const VIDEO_RATIO = {
  width: 9,
  height: 16,
}

type WidgetContextType = {
  goBack: any
  canGoBack: boolean
  goTo: any
  welcomeVideoPlayed: boolean
  setWelcomeVideoPlayed: any
  handleAnswerAction: any
  widgetLayout: WIDGET_LAYOUT
  setWidgetLayout: React.Dispatch<React.SetStateAction<WIDGET_LAYOUT>>
  toggleLayout: any
  isSelectedInCollection: any
  positionInCollection: number | undefined
  collectionReady: boolean
  collectionMuted: boolean
  prevCarouselScrollLeft: any
  isCollectionIntersecting: boolean
}

export const WidgetContext = createContext<WidgetContextType>(
  {} as WidgetContextType,
)
